<script setup lang="ts">
import type { CSSProperties } from 'vue'
import { pxToPt } from './utils/px-to-pt'
import { parsePadding } from './utils/parse-padding'

export interface EButtonProps {
  href?: string
  target?: string
  style?: CSSProperties
}

const props = withDefaults(defineProps<EButtonProps>(), {
  target: '_blank'
})

const MAX_FONT_WIDTH = 5

function computeFontWidthAndSpaceCount(expectedWidth: number): [number, number] {
  if (expectedWidth === 0) return [0, 0]

  let smallestSpaceCount = 0

  const computeRequiredFontWidth = () => {
    if (smallestSpaceCount > 0) {
      return expectedWidth / smallestSpaceCount / 2
    }
    return Number.POSITIVE_INFINITY
  }

  while (computeRequiredFontWidth() > MAX_FONT_WIDTH) {
    smallestSpaceCount++
  }

  return [computeRequiredFontWidth(), smallestSpaceCount]
}

const parsedPadding = computed(() => parsePadding(props.style ?? {}))

const textRaise = computed(() => {
  const y = (parsedPadding.value.paddingTop ?? 0) + (parsedPadding.value.paddingBottom ?? 0)
  return pxToPt(y)
})

const msoLeftHtml = computed(() => {
  const [fontWidth, spaceCount] = computeFontWidthAndSpaceCount(parsedPadding.value.paddingLeft ?? 0)
  if (spaceCount === 0) return ''
  const hairSpaces = '&#8202;'.repeat(spaceCount)
  return `<!--[if mso]><i style="mso-font-width:${fontWidth * 100}%;mso-text-raise:${textRaise.value}" hidden>${hairSpaces}</i><![endif]-->`
})

const msoRightHtml = computed(() => {
  const [fontWidth, spaceCount] = computeFontWidthAndSpaceCount(parsedPadding.value.paddingRight ?? 0)
  if (spaceCount === 0) return ''
  const hairSpaces = '&#8202;'.repeat(spaceCount)
  return `<!--[if mso]><i style="mso-font-width:${fontWidth * 100}%" hidden>${hairSpaces}&#8203;</i><![endif]-->`
})

const linkStyle = computed(
  (): CSSProperties => ({
    lineHeight: '100%',
    textDecoration: 'none',
    display: 'inline-block',
    maxWidth: '100%',
    ...props.style,
    paddingTop: parsedPadding.value.paddingTop,
    paddingRight: parsedPadding.value.paddingRight,
    paddingBottom: parsedPadding.value.paddingBottom,
    paddingLeft: parsedPadding.value.paddingLeft
  })
)

const innerSpanStyle = computed(
  (): CSSProperties => ({
    maxWidth: '100%',
    display: 'inline-block',
    lineHeight: '120%'
  })
)
</script>

<template>
  <a :href="href" :target="target" :style="linkStyle">
    <span v-if="msoLeftHtml" v-html="msoLeftHtml" />
    <span :style="innerSpanStyle">
      <slot />
    </span>
    <span v-if="msoRightHtml" v-html="msoRightHtml" />
  </a>
</template>
